import React from 'react';
import Select, { StylesConfig } from 'react-select';
import AsyncSelect from 'react-select/async';
import { SelectOptions } from '../../../../utils/types/react_select_types';

// Add this type for your options!

interface SelectProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  isMulti?: boolean;
  value?: SelectOptions | SelectOptions[];
  onChange: (value: any) => void;
  loadOptions?: (
    inputValue: string,
    callback: (options: SelectOptions[]) => void
  ) => void;
  options?: SelectOptions[];
  defaultOptions?: boolean | SelectOptions[];
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
  isDisabled?: boolean;
}

export const FloatingLabelSelect: React.FC<SelectProps> = ({
  label,
  placeholder = 'Search...',
  required = false,
  isMulti = false,
  value,
  onChange,
  loadOptions,
  options,
  defaultOptions,
  error = false,
  helperText = '',
  onBlur,
  isDisabled = false,
}) => {
  const isAsync = typeof loadOptions === 'function';
  const customStyles: StylesConfig<SelectOptions, boolean> = {
    control: (base, state) => ({
      ...base,
      minHeight: '36px',
      height: '36px',
      backgroundColor: state.isDisabled ? '#f8fafc' : '#fff',
      borderColor: error ? '#f87171' : state.isFocused ? '#10b981' : '#e2e8f0',
      borderWidth: '1px',
      boxShadow: state.isFocused ? '0 0 0 4px rgba(16, 185, 129, 0.1)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#10b981' : '#cbd5e1',
      },
      zIndex: 50,
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 12px',
      fontSize: '13px',
      fontWeight: '500',
      color: '#475569',
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      textAlign: 'left',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '4px 8px',
      color: '#94a3b8',
      '&:hover': {
        color: '#64748b',
      },
      svg: {
        width: '16px',
        height: '16px',
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '4px',
      svg: {
        width: '16px',
        height: '16px',
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: '0.5rem',
      boxShadow:
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      overflow: 'hidden',
      marginTop: '4px',
      border: '1px solid #e2e8f0',
    }),
    singleValue: (base) => ({
      ...base,
      textAlign: 'left',
      color: '#475569',
    }),
    placeholder: (base) => ({
      ...base,
      textAlign: 'left',
      color: '#94a3b8',
      fontSize: '13px',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#f0fdf4'
        : state.isFocused
          ? '#f8fafc'
          : 'transparent',
      color: state.isSelected ? '#059669' : '#475569',
      cursor: 'pointer',
      fontSize: '13px',
      padding: '8px 12px',
      textAlign: 'left',
      ':active': {
        backgroundColor: '#f0fdf4',
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#ecfdf5',
      borderRadius: '9999px',
      fontSize: '11px',
      border: '1px solid #d1fae5',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#059669',
      padding: '1px 8px',
      fontWeight: '600',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#10b981',
      borderRadius: '9999px',
      ':hover': {
        backgroundColor: '#10b981',
        color: '#ffffff',
      },
    }),
  };

  return (
    <div className='w-full'>
      { label && (
        <label className='block mb-1 text-[11px] font-semibold text-slate-500 uppercase tracking-wider ml-0.5'>
          { label } { required && <span className='text-red-500'>*</span> }
        </label>
      ) }

      { isAsync ? (
        <AsyncSelect
            cacheOptions
            defaultOptions={defaultOptions}
            isMulti={isMulti}
            value={value}
            onChange={onChange}
            loadOptions={loadOptions}
            placeholder={placeholder}
            onBlur={onBlur}
            styles={customStyles}
            menuPortalTarget={document.body}
            isDisabled={isDisabled}
        />
      ) : (
        <Select
            options={options}
            isMulti={isMulti}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            onBlur={onBlur}
            styles={customStyles}
            menuPortalTarget={document.body}
            isDisabled={isDisabled}
        />
      ) }

      { error && helperText && (
        <p className='mt-0.5 text-[10px] text-red-500 font-medium ml-0.5'>
          { helperText }
        </p>
      ) }
    </div>
  );
};
